import { SubmitVoucherCode } from "@/hooks/api/review-api/apply-promo";
import { QuoteData } from "@/types/quote/quote-data";
import { PolicyTypeKey } from "@/types/review-types";

export const voucherAmount = (attributes: QuoteData["attributes"]) => {
  return attributes?.paymentDetails?.discounts?.voucher
    ?.filter((voucher) => !voucher.message)
    .map((voucher) => {
      return {
        index: voucher.index,
        code: voucher.code,
      };
    });
};

export const promoReturnMessage = (
  attributes: QuoteData["attributes"],
  showValidationPromo: boolean,
  promoCode: string,
  promoErrorCodeAlreadyApplied: string,
  promoSuccessMassage: string,
  promoErrorCodeLimitReached: string,
  promoErrorCodeInvalid: string
) => {
  if (!showValidationPromo) return { message: "", state: "" };
  const code = attributes?.paymentDetails?.discounts?.promoCode;
  if (code?.code === promoCode && !code?.valid) {
    return { message: promoErrorCodeInvalid, state: "error" };
  }
  if (code?.code === promoCode) {
    return { message: promoErrorCodeAlreadyApplied, state: "error" };
  }
  if (code?.valid && !promoCode) {
    return { message: promoSuccessMassage, state: "success" };
  }
  if (code?.valid && promoCode) {
    return { message: promoErrorCodeLimitReached, state: "error" };
  }
  if (code && !code?.valid && !promoCode) {
    return { message: promoErrorCodeInvalid, state: "error" };
  }

  return { message: "", state: "" };
};

export const voucherReturnMessage = (
  attributes: QuoteData["attributes"],
  newVoucherCode: SubmitVoucherCode | undefined,
  validVouchers: unknown[] | undefined,
  showValidationVoucher: boolean,
  voucherErrorAlreadyTwoCodeApplied: string,
  voucherErrorCodeApplied: string,
  voucherErrorInvalidCode: string,
  voucherSuccessMassage: string,
  voucherErrorCodeLimitReached: string
) => {
  const code = attributes?.paymentDetails?.discounts?.voucher;
  if (!showValidationVoucher) return { message: "", state: "" };
  if (validVouchers && validVouchers?.length >= 2 && newVoucherCode?.code) {
    return { message: voucherErrorAlreadyTwoCodeApplied, state: "error" };
  }
  if (
    code?.some((item) => item.code === newVoucherCode?.code && !item.message)
  ) {
    return { message: voucherErrorCodeApplied, state: "error" };
  }
  if (code?.some((item) => !!item.message) && !newVoucherCode?.code) {
    return { message: voucherErrorInvalidCode, state: "error" };
  }
  if (code?.some((item) => !item.message) && !newVoucherCode?.code) {
    return { message: voucherSuccessMassage, state: "success" };
  }
  if (validVouchers && validVouchers?.length >= 2) {
    return { message: voucherErrorCodeLimitReached, state: "error" };
  }

  return { message: "", state: "" };
};

export function addVoucherCode(
  newCode: SubmitVoucherCode | undefined,
  attributes: QuoteData["attributes"],
  validVouchers: SubmitVoucherCode[] | undefined,
  voucherCode: SubmitVoucherCode[]
): SubmitVoucherCode[] {
  if (!newCode) return voucherCode;

  if (validVouchers && validVouchers?.length >= 2) {
    return validVouchers;
  }

  const replaceCode =
    attributes?.paymentDetails?.discounts?.voucher
      ?.filter((voucher) => !!voucher.message)
      .map((voucher) => {
        return { index: voucher.index, code: newCode.code };
      })
      .slice(0, 1) ?? [];

  const replaceVoucher = replaceCode[0]
    ? replaceCode
    : [{ index: newCode.index, code: newCode.code }];

  return [...replaceVoucher, ...(validVouchers ?? [])];
}

export function formatPolicyStartDate(
  policyStartDate: string | null | undefined
): string {
  // Check if the input is null, undefined, or not a string
  if (!policyStartDate || typeof policyStartDate !== "string") return "";

  // Validate if the input is a valid date
  const date = new Date(policyStartDate);
  if (Number.isNaN(date.getTime())) return ""; // Invalid date check

  // Format the date as DD/MM/YYYY
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

export function validatePassportId(
  value: string,
  inputPassportIdErrorEmpty?: string,
  inputPassportIdErrorInvalid?: string
) {
  let err = "";

  if (!value.trim()) {
    err = inputPassportIdErrorEmpty ?? "Passport ID cannot be empty";
  } else if (!/^[0-9A-Za-z]{5,12}$/.test(value)) {
    err = inputPassportIdErrorInvalid ?? "Enter a valid passport ID";
  }
  return err;
}

export function isEmptyString(value: string | null | undefined): boolean {
  return value === null || value === undefined || value?.trim() === "";
}

/**
 * Removes all whitespace characters (spaces, tabs, line breaks, etc.) from a string
 * @param value - The string to remove spaces from
 * @returns The string with all whitespace removed
 * @example
 * removeAllSpaces(" TEST 20 ") // Returns "TEST20"
 * removeAllSpaces("TE ST 20") // Returns "TEST20"
 */
export function removeAllSpaces(value: string): string {
  return value.replace(/\s/g, "");
}

/**
 * Normalizes postageOption value: converts "-1" or -1 to "0"
 * Backend sometimes returns -1, which should be treated as "0" (Email only option)
 * Returns undefined for null, undefined, or empty string values
 */
export const normalizePostageOption = (
  value: string | number | null | undefined
): string | undefined => {
  // Handle null, undefined, or empty string
  if (value === null || value === "") {
    return undefined;
  }

  // Convert -1 (string or number) to "0"
  if (value === "-1" || value === -1) {
    return "0";
  }

  // Convert to string for consistency
  return String(value);
};
